import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  ReferenceType,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  // useFloatingNodeId,
  // FloatingNode,
  // FloatingTree,
  UseFloatingReturn,
  Placement,
  useFloatingNodeId,
  FloatingNode,
  useFloatingParentNodeId,
  FloatingTree,
  UseDismissProps,
} from "@floating-ui/react";
import { cloneElement, useEffect, useState } from "react";
import { zIndex } from "../../../api/CommonConstants";

const overflowPadding = 30;

export const FloatingDiv = (props: FloatingDivProps) => {
  const parentId = useFloatingParentNodeId();

  // This is a root, so we wrap it with the tree
  if (parentId === null) {
    return (
      <FloatingTree>
        <FloatingDivComponent {...props} />
      </FloatingTree>
    );
  }

  return <FloatingDivComponent {...props} />;
};

export interface FloatingDivProps {
  defaultOpen?: boolean;
  enabled?: boolean;
  disableToggle?: boolean;
  onOpenChange?: (open: boolean) => void;
  outsidePress?: UseDismissProps["outsidePress"];
  triggerComponent: <T extends ReferenceType>(props: {
    context: UseFloatingReturn<T>["context"];
    ref: ((node: ReferenceType | null) => void) & ((node: T | null) => void);
    getReferenceProps: (userProps?: React.HTMLProps<Element> | undefined) => Record<string, unknown>;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isOpen: boolean;
  }) => React.ReactElement;
  children: ({
    open,
    setOpen,
  }: // context,
  {
    // context: UseFloatingReturn<T>["context"];
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }) => React.ReactNode;
  placement?: Placement;
}
/**
 * A generic floating div component that displays a floating element (like a dropdown or tooltip)
 * anchored to a trigger component. Handles open/close state, positioning, focus management,
 * and outside click dismissal.
 * @author @CorradoSurmanowicz
 * @template T - The type of the HTMLElement used as the floating reference.
 *
 * @param {boolean} [defaultOpen=false] - Whether the floating element is open by default.
 * @param {boolean} [enabled=true] - Whether the floating element is enabled.
 * @param {boolean} [disableToggle] - If true, disables toggling the open state via trigger.
 * @param {(open: boolean) => void} [onOpenChange] - Callback invoked when the open state changes.
 * @param {boolean | ((event: MouseEvent | TouchEvent) => boolean)} [outsidePress] - Controls outside press behavior for dismissing the floating element.
 * @param {Function} triggerComponent - A render prop function that returns the trigger element.
 * @param {Function} children - A render prop function that returns the floating content.
 * @param {"top" | "bottom" | "left" | "right"} [placement="bottom"] - The preferred placement of the floating element.
 *
 * @returns {JSX.Element} The floating div component with trigger and floating content.
 *
 * @remarks
 * - Integrates with a floating UI system for advanced positioning and accessibility.
 * - Uses context and middleware for offset, flipping, and sizing.
 * - Manages focus and keyboard navigation for accessibility.
 */
const FloatingDivComponent = <T extends HTMLElement>({
  defaultOpen = false,
  enabled = true,
  disableToggle,
  onOpenChange,
  outsidePress,
  triggerComponent,
  children,
  placement = "bottom",
}: FloatingDivProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const [pointer, setPointer] = useState(false);

  if (!open && pointer) {
    setPointer(false);
  }

  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  useEffect(() => {
    onOpenChange?.(open);
  }, [onOpenChange, open]);

  // Subscribe this component to the <FloatingTree> wrapper:
  const nodeId = useFloatingNodeId();

  const { refs, floatingStyles, context } = useFloating<T>({
    nodeId,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    placement,
    middleware: [
      offset(5),
      flip({ padding: overflowPadding }),
      size({
        padding: overflowPadding,
      }),
    ],
  });

  const click = useClick(context, { toggle: !disableToggle, enabled: enabled });
  const role = useRole(context, { role: "listbox" });
  const dismiss = useDismiss(context, {
    outsidePress: outsidePress,
  });

  const { getReferenceProps } = useInteractions([click, role, dismiss]);

  return (
    <>
      {cloneElement(
        triggerComponent({
          context: context,
          ref: refs.setReference,
          getReferenceProps: getReferenceProps,
          setOpen: setOpen,
          isOpen: open,
        }),
        { ref: refs.setReference }
      )}
      <FloatingNode id={nodeId}>
        {open && (
          <FloatingPortal>
            <FloatingFocusManager context={context} modal={false} initialFocus={refs.floating} order={["reference"]}>
              <div
                ref={refs.setFloating}
                tabIndex={-1}
                style={{
                  outline: "none",
                  ...floatingStyles,
                  zIndex: zIndex.zIndexPortalDropdown,
                }}
              >
                {children({
                  open: open,
                  setOpen: setOpen,
                })}
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </FloatingNode>
    </>
  );
};
